import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { Button, Checkbox, cn, Select, type SelectOption } from '@/shared/ui'
import { focusRing } from '@/shared/ui/cn'
import { ArrowUpDown, Check, ChevronDown, X } from '@/shared/ui/icons'

import { labelText, shownColumns, type SearchColumn } from './definition'
import { usePopoverA11y } from './use-popover-a11y'

/**
 * The column chooser: the mockups' Output Options popover (`SearchTools.jsx`), legacy's output
 * options. A checkbox per column, the locked ones fixed on; "Sort Alphabetically" orders the list
 * itself; and "Display times in" picks the time zone the results and the export show dates in.
 */
export function ColumnChooser<T>(props: {
  readonly columns: readonly SearchColumn<T>[]
  /** The chosen column ids, or `null` for the defaults. */
  readonly chosen: readonly string[] | null
  readonly onChoose: (columns: readonly string[]) => void
  readonly timeZone: string
  readonly onTimeZone: (timeZone: string) => void
}) {
  const intl = useIntl()
  const { columns, chosen, onChoose } = props
  const [open, setOpen] = useState(false)
  const [alphabetical, setAlphabetical] = useState(false)
  const panelRef = useRef<HTMLDialogElement>(null)
  const close = useCallback(() => setOpen(false), [])
  const toggleOpen = useCallback(() => setOpen((was) => !was), [])
  const toggleAlphabetical = useCallback(() => setAlphabetical((was) => !was), [])
  usePopoverA11y(open, panelRef, close)

  const labeled = useMemo(
    () => columns.map((column) => ({ column, label: labelText(intl, column) })),
    [columns, intl],
  )
  const ordered = useMemo(
    () => (alphabetical ? labeled.toSorted((a, b) => a.label.localeCompare(b.label, intl.locale)) : labeled),
    [labeled, alphabetical, intl.locale],
  )
  const shownIds = useMemo(() => shownColumns(columns, chosen).map((column) => column.id), [columns, chosen])
  const shown = useMemo(() => new Set(shownIds), [shownIds])

  // A new column goes last; the last column shown cannot be unchecked, since a search always shows one.
  const toggle = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onChoose([...shownIds.filter((shownId) => shownId !== id), id])
      } else if (shownIds.length > 1) {
        onChoose(shownIds.filter((shownId) => shownId !== id))
      }
    },
    [shownIds, onChoose],
  )

  const title = intl.formatMessage({ id: 'search.columns.title' })
  const closeLabel = intl.formatMessage({ id: 'search.columns.close' })

  return (
    <div className="relative">
      <Button variant="outlined" size="small" onClick={toggleOpen} aria-expanded={open}>
        {title}
        <ChevronDown size={18} className={cn('transition', open && 'rotate-180')} />
      </Button>

      {open && (
        <>
          {/* Pointer-only click-away; the keyboard closes the popover with Escape. */}
          <div aria-hidden="true" onClick={close} className="fixed inset-0 z-40 cursor-default" />
          <dialog
            open
            ref={panelRef}
            aria-label={title}
            tabIndex={-1}
            className="absolute left-auto right-0 top-full z-50 m-0 mt-2 flex max-h-[calc(100dvh-10rem)] w-80 max-w-[calc(100vw-2rem)] flex-col rounded border border-line bg-background p-0 text-text-primary shadow-card-hover"
          >
            <div className="flex items-center justify-between border-b border-divider px-4 py-3">
              <h2 className="font-heading text-base font-medium text-text-primary">{title}</h2>
              <button
                type="button"
                onClick={close}
                aria-label={closeLabel}
                className={cn('cursor-pointer rounded text-text-secondary hover:text-text-primary', focusRing)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-3 px-4 pt-4">
              <button
                type="button"
                onClick={toggleAlphabetical}
                aria-pressed={alphabetical}
                className={cn(
                  'flex w-fit cursor-pointer items-center gap-2 rounded text-sm transition hover:underline',
                  alphabetical ? 'font-medium text-primary-main' : 'text-text-primary',
                  focusRing,
                )}
              >
                <ArrowUpDown size={18} />
                {intl.formatMessage({ id: 'search.columns.sortAlpha' })}
              </button>
            </div>

            {/* Only the list scrolls, with a slim brand-green scroll bar. */}
            <ul aria-label={intl.formatMessage({ id: 'search.columns.list' })} className="mx-4 my-3 flex max-h-64 min-h-0 flex-col gap-2.5 overflow-y-auto overscroll-contain pr-2 [scrollbar-color:var(--color-primary-main)_transparent] [scrollbar-width:thin]">
              {ordered.map(({ column, label }) => (
                <li key={column.id}>
                  {column.locked === true || (shownIds.length === 1 && shown.has(column.id)) ? (
                    // A real checkbox, checked and disabled, so assistive technology hears it is fixed on.
                    <label className="inline-flex items-center gap-2 text-sm text-text-light">
                      <input type="checkbox" checked disabled readOnly className="sr-only" />
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border border-border-strong bg-background-contrast-md">
                        <Check size={12} />
                      </span>
                      {label}
                    </label>
                  ) : (
                    <ColumnOption id={column.id} label={label} checked={shown.has(column.id)} onToggle={toggle} />
                  )}
                </li>
              ))}
            </ul>

            <div className="border-t border-divider px-4 py-3">
              <TimeZoneSelect timeZone={props.timeZone} onTimeZone={props.onTimeZone} />
            </div>
          </dialog>
        </>
      )}
    </div>
  )
}

function ColumnOption(props: {
  readonly id: string
  readonly label: string
  readonly checked: boolean
  readonly onToggle: (id: string, checked: boolean) => void
}) {
  const { id, onToggle } = props
  const change = useCallback((checked: boolean) => onToggle(id, checked), [id, onToggle])
  return <Checkbox checked={props.checked} onChange={change} label={props.label} />
}

/** Every IANA time zone the browser knows, each with its name in the viewer's language. */
function TimeZoneSelect(props: { readonly timeZone: string; readonly onTimeZone: (timeZone: string) => void }) {
  const intl = useIntl()
  const current = props.timeZone
  const zones = useMemo<readonly SelectOption<string>[]>(() => {
    const known = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
    // UTC is always offered, as the mockup's "Coordinated Universal Time", whatever the browser lists.
    const all = [...new Set([current, 'UTC', ...known])]
    const instant = new Date()
    return all.map((zone) => {
      const name = new Intl.DateTimeFormat(intl.locale, { timeZone: zone, timeZoneName: 'longGeneric' })
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')?.value
      return {
        value: zone,
        label: name === undefined ? zone : intl.formatMessage({ id: 'search.columns.zone' }, { name, zone }),
      }
    })
  }, [current, intl])

  return (
    <Select
      label={intl.formatMessage({ id: 'search.columns.timeZone' })}
      value={current}
      onChange={props.onTimeZone}
      options={zones}
    />
  )
}
